import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GetServiceCaller, PostFormServiceCaller } from "../../../../services/serviceCaller";
import { API_URL } from "../../../../constants";
import Dynamic_PR_table from "../../../../components/Dynamic_PR_table";
import { useDispatch } from "react-redux";
import { updateNavbarHeading } from "../../../../constants/layout/store/filterSlice";
import { toast } from "react-toastify";
import moment from "moment";

const AddAssetList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const passedState = location.state;


  // State declarations
  const [addAssetListData, setAddAssetListData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryTypeOption, setCategoryTypeOption] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assetTypeOption, setAssetTypeOption] = useState([]);
  const [allocatedOption, setAllocatedOption] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filterFormData, setFilterData] = useState([]);
  const [apiStatus, setApiStatus] = useState(undefined);

  // Table columns configuration
  const columns = {
    name: { text: "Name", align: "start", visible: true },
    Description: { text: "Description", align: "left", visible: false },
    AssetTypes: { text: "Type", align: "start", visible: true },
    CategoryName: { text: "Category", align: "start", visible: true },
    Brand: { text: "Make", align: "start", visible: true },
    Model: { text: "Model", align: "start", visible: true },
    InvoiceNumber: { text: "Invoice No.", align: "end", visible: true },
    VendorName: { text: "Vendor", align: "left", visible: true },
    AssetPrice: { text: "Price", align: "end", visible: true },
    Allocated: { text: "Status", align: "left", visible: true },
    PurchaseDate: { text: "Purchase Date", align: "center", visible: true },
    WarrantyDate: { text: "Warranty Date", align: "center", visible: true },
  };

  // Form fields configuration
  const formDatas = [
    {
      name: "Asset Type",
      type: "Select",
      placeholderText: "Select",
      options: [{ value: "", label: "Select" }, ...assetTypeOption],
    },
    {
      name: "Category",
      type: "Select",
      placeholderText: "Select",
      options: [{ value: "", label: "Select" }, ...(categoryTypeOption || [])],
    },
    {
      name: "Allocation Type",
      placeholderText: "Select ",
      type: "Select",
      options: [{ value: "", label: "Select" }, ...(allocatedOption || [])],
    },
  ];

  // Set navbar heading on mount
  useEffect(() => {
    dispatch(updateNavbarHeading(`${passedState ? passedState?.text : "Assets"}`));
  }, [dispatch]);

  // Fetch asset data
  const fetchGetAddAsset = useCallback(async () => {
    try {
      const response = await GetServiceCaller(
        `${API_URL}asset?P_AssetType=&P_CategoryID_1160=0&P_Allocated=&P_AssetID=0`
      );
      setApiStatus(response?.status);

      if (response.status === 200) {
        const cleanedData = response?.data?.data;

        const assetList = Object.entries(response?.data?.assetType || {}).map(
          ([key, value]) => ({
            value: parseInt(key, 10),
            label: value,
          })
        );

        const allocated = Object.entries(response?.data?.allocatedType || {}).map(
          ([key, value]) => ({
            value: parseInt(key, 10),
            label: value,
          })
        );

        setAssetTypeOption(assetList);
        setAllocatedOption(allocated);
        setOriginalData(cleanedData);
        setAddAssetListData(cleanedData);
        setLoading(false);
      } else {
        console.error("Failed to fetch data. Server responded with status:", response.status);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchGetAddAsset();
  }, [fetchGetAddAsset]);

  // Apply URL filters
  useEffect(() => {
    if (originalData.length === 0) return;

    const queryParams = new URLSearchParams(location.search);
    const pAllocatedParam = queryParams.get('P_Allocated');
    const pCategoryParam = queryParams.get('CategoryName');

    if (pAllocatedParam) {
      const filteredData = originalData.filter(o =>
        o.AllocatedId == pAllocatedParam && 
        o.CategoryName == pCategoryParam
      );
      setAddAssetListData(filteredData);
    } else {
      setAddAssetListData(originalData);
    }
  }, [location.search, originalData]);

  // Fetch category details
  const fetchGetCategoryDetails = useCallback(async (id) => {
    try {
      const queryParams = new URLSearchParams({ AssetType: id });
      const response = await GetServiceCaller(
        `${API_URL}get-asset-categories?${queryParams.toString()}`
      );

      if (response.status === 200) {
        const categoryList = response?.data?.data?.map((ele) => ({
          value: ele?.ID,
          label: ele?.CategoryName,
        }));
        setCategoryTypeOption(categoryList);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  // Handle category changes
  useEffect(() => {
    const assetType = filterFormData.find((o) => o.label === "Asset Type")?.value;
    if (assetType !== undefined && assetType !== null) {
      fetchGetCategoryDetails(assetType);
    }
  }, [filterFormData, fetchGetCategoryDetails]);

  // Filter change handler
  const handleFilterChange = (e, name) => {
    setFilterData((prevFormData) => {
      const existingIndex = prevFormData.findIndex((item) => item.label === name);
      if (existingIndex !== -1) {
        return prevFormData.map((item) =>
          item.label === name
            ? { ...item, value: e.value !== undefined ? e.value : e }
            : item
        );
      }
      return [
        ...prevFormData,
        { label: name, value: e.value !== undefined ? e.value : e },
      ];
    });
  };

  // Get filtered data
  const handleGetData = async () => {
    try {
      const assetType = filterFormData.find((o) => o.label === "Asset Type")?.value || '';
      const category = filterFormData.find((o) => o.label === "Category")?.value || '';
      const allocationType = filterFormData.find((o) => o.label === "Allocation Type")?.value || '';

      const response = await GetServiceCaller(
        `${API_URL}asset?P_AssetType=${assetType}&P_CategoryID_1160=${category}&P_Allocated=${allocationType}&P_AssetID=0`
      );

      if (response.status === 200) {
        setAddAssetListData(response?.data?.data);
        setOriginalData(response?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers
  const handleAddNewAsset = () => navigate(`/add-asset/add`);
  const handleEdit = (id) => navigate(`/add-asset/edit/${id.ID}`);
  const handleHistory = (id) => navigate(`/add-asset/history/${id.ID}`);
  const handleApproveClick = (id) => {
    setSelectedId(id?.ID);
    setShowModal(true);
  };

  // Approval actions
  const handleApproveAction = useCallback(async (status) => {
    try {
      const response = await PostFormServiceCaller(
        `${API_URL}assets/${selectedId}/dispose?status=${status}`,
        'post'
      );
      toast.success(status === 1 ? 'Approved successfully!' : 'Rejected successfully!');
      fetchGetAddAsset();
    } catch (error) {
      toast.error(status === 1 ? 'Approval failed' : 'Rejection failed');
    } finally {
      setShowModal(false);
    }
  }, [selectedId, fetchGetAddAsset]);

  const handleApprove = (e) => {
    e.preventDefault();
    handleApproveAction(1);
  };

  const handleReject = () => handleApproveAction(0);

  return (
    <div className="container-fluid" id="skill_section">
      <div className="rounded bg-white" id="skill-table-area">
        <div className="row">
          <div className="text-end">
            <button
              className="custom-btn"
              onClick={() => navigate(`/add-asset/import`)}
            >
              Import Asset
            </button>
          </div>
        </div>
        <div className="row mt-2">
          <Dynamic_PR_table
            data={addAssetListData}
            columns={columns}
            itemsPerPage={10}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            isApiStatus={apiStatus}
            isRequiredStatus={true}
            showIcon1={true}
            showIcon2={true}
            isSorting={true}
            formDatas={formDatas}
            filterFormData={filterFormData}
            handleGetData={handleGetData}
            isFilterRequired={true}
            isActionRequired={true}
            handleChange={handleFilterChange}
            isAddNewBtnRequired={true}
            showIcon3={true}
            showIcon4={true}
            showIcon5={false}
            handleAddNew={handleAddNewAsset}
            handleIcon2={handleApproveClick}
            handleIcon3={handleEdit}
            handleIcon4={handleHistory}
            icons={{
              Icon2: <i className="fa fa-check"></i>,
              Icon2Text: "Approve",
              Icon2Flag: "canApprove",
              Icon3: <i className="bi bi-pencil-square icons"></i>,
              Icon3Text: "Edit",
              Icon4: <i className="fa fa-history" aria-hidden="true"></i>,
              Icon4Text: "History",
            }}
          />
        </div>

        {showModal && (
          <div className="modal" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <form onSubmit={handleApprove}>
                    <h2>Approve</h2>
                    <div className="text-end mt-4">
                      <button
                        type="button"
                        className="custom-btn me-2"
                        onClick={handleReject}
                      >
                        No
                      </button>
                      <button className="custom-btn" type="submit">
                        Yes
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddAssetList;